/**
 * Reports Service
 * Aggregation queries against ApiRequestLogs for the Reports page.
 * Counts and totals; the page drills from these into search-history rows.
 */

import { makeAuthenticatedRequest, createApiError } from './apiService';
import { API_ENDPOINTS } from '../config/api';

export interface ReportsFilters {
  vin?: string;
  requestType?: string;
  startDate?: string;
  endDate?: string;
  orgWide?: boolean;
  // Drill-down filters (narrow the caller's scope, never widen it)
  state?: string;
  filterUserId?: string;
  integrationOnly?: boolean;
}

export interface ReportsByUserRow {
  userId: string | null;
  username: string;
  email: string;
  count: number;
  totalCost: number;
}

export interface ReportsByTypeRow {
  type: string;
  count: number;
  totalCost: number;
}

export interface ReportsByDayRow {
  date: string;
  count: number;
  totalCost: number;
}

export interface ReportsByStateRow {
  stateCode: string;
  count: number;
  totalCost: number;
}

export interface ReportsAggregateResponse<T> {
  items: T[];
  totalCount: number;
  totalCost: number;
}

const buildQueryString = (filters: ReportsFilters): string => {
  const params = new URLSearchParams();
  if (filters.vin) params.append('vin', filters.vin);
  if (filters.requestType) params.append('requestType', filters.requestType);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.orgWide) params.append('orgWide', 'true');
  if (filters.state) params.append('state', filters.state);
  if (filters.filterUserId) params.append('filterUserId', filters.filterUserId);
  if (filters.integrationOnly) params.append('integrationOnly', 'true');
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

const fetchJson = async <T>(url: string): Promise<T> => {
  const response = await makeAuthenticatedRequest(url, { method: 'GET' });
  if (!response.ok) throw await createApiError(response);
  return await response.json() as T;
};

export const getReportsByUser = (filters: ReportsFilters) =>
  fetchJson<ReportsAggregateResponse<ReportsByUserRow>>(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_USER}${buildQueryString(filters)}`);

export const getReportsByType = (filters: ReportsFilters) =>
  fetchJson<ReportsAggregateResponse<ReportsByTypeRow>>(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_TYPE}${buildQueryString(filters)}`);

export const getReportsByDay = (filters: ReportsFilters) =>
  fetchJson<ReportsAggregateResponse<ReportsByDayRow>>(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_DAY}${buildQueryString(filters)}`);

export const getReportsByState = (filters: ReportsFilters) =>
  fetchJson<ReportsAggregateResponse<ReportsByStateRow>>(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_STATE}${buildQueryString(filters)}`);

const downloadCsv = async (url: string, filename: string): Promise<void> => {
  const response = await makeAuthenticatedRequest(url, { method: 'GET' });
  if (!response.ok) throw await createApiError(response);
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  const cd = response.headers.get('Content-Disposition');
  let resolved = filename;
  if (cd) {
    const match = cd.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match && match[1]) resolved = match[1].replace(/['"]/g, '');
  }
  link.download = resolved;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

export const exportReportsByUserCsv = (filters: ReportsFilters) =>
  downloadCsv(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_USER_CSV}${buildQueryString(filters)}`, 'Reports_ByUser.csv');

export const exportReportsByTypeCsv = (filters: ReportsFilters) =>
  downloadCsv(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_TYPE_CSV}${buildQueryString(filters)}`, 'Reports_ByType.csv');

export const exportReportsByDayCsv = (filters: ReportsFilters) =>
  downloadCsv(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_DAY_CSV}${buildQueryString(filters)}`, 'Reports_ByDay.csv');

export const exportReportsByStateCsv = (filters: ReportsFilters) =>
  downloadCsv(`${API_ENDPOINTS.API_LOGS.REPORTS_BY_STATE_CSV}${buildQueryString(filters)}`, 'Reports_ByState.csv');
